import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmt, fmtDate, BILL_CATEGORIES, catColor, catLabel } from "@/lib/constants";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { CreditCard, TrendingDown, Wallet, CalendarCheck2, ArrowUpRight, CheckCircle2, Coins } from "lucide-react";
import { Link } from "react-router-dom";
import DebtProgressCard from "@/components/DebtProgressCard";
import UtilizationKPI from "@/components/UtilizationKPI";
import GettingStarted from "@/components/GettingStarted";
import DoThisNext from "@/components/DoThisNext";
import DashboardWelcome from "@/components/DashboardWelcome";
import HelpTip from "@/components/HelpTip";
import { INTENT, PROGRESS_BAR } from "@/lib/intent";
import { useAuth } from "@/context/AuthContext";

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  const load = () => api.get("/dashboard/summary").then(r => setData(r.data));
  useEffect(() => { load(); }, []);

  if (!data) return <SkeletonDash />;

  const total = data.paid_count + data.unpaid_count;
  const pct = total ? Math.round((data.paid_count / total) * 100) : 0;
  const payoffDate = data.debt_progress?.totals?.projected_payoff_date || data.projected_payoff_date;
  const isEmpty = total === 0 && (data.debt_progress?.totals?.debts_total || 0) === 0;

  if (isEmpty) {
    return (
      <div className="space-y-6" data-testid="dashboard-page">
        <header>
          <div className="text-[10px] uppercase tracking-[0.2em] text-teal-500 font-semibold">Command Center</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Your money at a glance</h1>
        </header>
        <DashboardWelcome name={user?.name} />
      </div>
    );
  }

  const pieData = data.category_breakdown.map(c => ({
    name: catLabel(BILL_CATEGORIES, c.category),
    value: c.amount,
    color: catColor(BILL_CATEGORIES, c.category),
  }));

  // Color intent: on-track = green, attention = amber, informational = neutral.
  const payoffIntent = !payoffDate ? "neutral" : (data.debt_progress?.plan ? "good" : "warn");

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <header>
        <div className="text-[10px] uppercase tracking-[0.2em] text-teal-500 font-semibold">Command Center</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Your money at a glance</h1>
      </header>

      <GettingStarted data={data} />

      <DoThisNext action={data.next_action} />

      {/* Featured — the two numbers that matter most: what to pay now, and when you're free */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <KPI featured icon={CalendarCheck2} intent="neutral" label={<>Min due this month <HelpTip k="min_due" /></>} value={fmt(data.total_min_this_month)} sub="Minimum payments due before month-end" testid="kpi-min-month" />
        <KPI featured icon={CreditCard} intent={payoffIntent} label={<>Projected debt-free <HelpTip k="debt_free" /></>} value={payoffDate ? fmtDate(payoffDate) : "—"} sub={data.debt_progress?.plan ? `On track · ${data.debt_progress.plan.strategy} plan` : "Minimums only — set a plan to go faster"} testid="kpi-payoff" />
      </div>

      {/* Secondary — supporting context, compact */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <KPI compact icon={Coins} intent="neutral" label="Monthly commitment" value={fmt(data.debt_progress?.totals?.monthly_commitment || 0)} sub="minimums + snowball" testid="kpi-monthly-commitment" />
        <KPI compact icon={TrendingDown} intent="neutral" label="Total Debt" value={fmt(data.total_debt)} sub={`Avg APR ${data.average_apr}%`} testid="kpi-total-debt" />
        <UtilizationKPI compact credit={data.debt_progress?.credit} />
      </div>

      {data.debt_progress && <DebtProgressCard progress={data.debt_progress} />}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Progress + upcoming */}
        <div className="lg:col-span-2 space-y-4">
          <div className="p-6 rounded-2xl border border-border bg-card">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-display text-lg font-bold">Bills paid this cycle</h3>
                <div className="text-xs text-muted-foreground">{data.paid_count} of {total} bills marked paid</div>
              </div>
              <div className="text-3xl font-display font-extrabold tabular">{pct}%</div>
            </div>
            <div className="h-3 bg-accent rounded-full overflow-hidden">
              <div className={`h-full ${PROGRESS_BAR} transition-all`} style={{ width: `${pct}%` }} />
            </div>
          </div>

          <div className="p-6 rounded-2xl border border-border bg-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-bold">Next 7 days</h3>
              <Link to="/calendar" className="text-xs text-teal-500 hover:underline flex items-center gap-1">View calendar <ArrowUpRight className="h-3 w-3" /></Link>
            </div>
            {data.upcoming_bills.length === 0 ? (
              <EmptyRow icon={CheckCircle2} title="You're clear this week" hint="No bills due in the next 7 days." />
            ) : (
              <ul className="divide-y divide-border" data-testid="upcoming-list">
                {data.upcoming_bills.map(b => (
                  <li key={b.id} className="py-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: catColor(BILL_CATEGORIES, b.category) }} />
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{b.name}</div>
                        <div className="text-xs text-muted-foreground">{catLabel(BILL_CATEGORIES, b.category)} · Due {fmtDate(b.pay_date || b.due_date)}{b.weekend_shifted && <span className="text-amber-500" data-testid={`dash-weekend-shift-${b.id}`}> · moved off {b.shift_reason && b.shift_reason !== "weekend" ? b.shift_reason : "weekend"}</span>}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono font-semibold">{fmt(b.amount)}</div>
                      <div className={`text-[10px] font-semibold uppercase tracking-wider ${b.days_until <= 2 ? "text-rose-500" : "text-amber-500"}`}>
                        {b.days_until === 0 ? "Today" : b.days_until === 1 ? "Tomorrow" : `${b.days_until} days`}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Category breakdown */}
        <div className="p-6 rounded-2xl border border-border bg-card">
          <h3 className="font-display text-lg font-bold mb-2">By category</h3>
          <div className="text-xs text-muted-foreground mb-4">Distribution of your bills</div>
          {pieData.length === 0 ? (
            <EmptyRow title="No bills yet" hint="Add a bill to see the breakdown." />
          ) : (
            <div className="h-64" data-testid="category-pie">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}>
                    {pieData.map((e) => <Cell key={e.name} fill={e.color} stroke="hsl(var(--card))" strokeWidth={2} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmt(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, sub, intent = "neutral", testid, featured, compact }) {
  const c = INTENT[intent] || INTENT.neutral;
  const pad = featured ? "p-6" : compact ? "p-4" : "p-5";
  const valueSize = featured ? "text-3xl sm:text-4xl" : compact ? "text-xl" : "text-2xl";
  const iconBox = compact ? "h-8 w-8" : "h-9 w-9";
  return (
    <div data-testid={testid} className={`${pad} rounded-2xl border border-border bg-card relative overflow-hidden group hover:-translate-y-0.5 transition-all`}>
      <div className={`absolute -top-10 -right-10 h-32 w-32 rounded-full bg-gradient-to-br ${c.glow} blur-2xl opacity-70`} />
      <div className={`${iconBox} rounded-lg bg-accent flex items-center justify-center ${c.icon}`}><Icon className="h-4 w-4" /></div>
      <div className={`${compact ? "mt-2.5" : "mt-3"} text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1`}>{label}</div>
      <div className={`mt-1 font-display ${valueSize} font-extrabold font-mono tracking-tight ${c.value}`}>{value}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>
    </div>
  );
}

function EmptyRow({ icon: Icon, title, hint }) {
  return (
    <div className="py-8 text-center text-sm text-muted-foreground">
      {Icon && <Icon className="h-6 w-6 mx-auto mb-2 text-teal-500" />}
      <div className="font-semibold text-foreground">{title}</div>
      <div className="text-xs mt-1">{hint}</div>
    </div>
  );
}

function SkeletonDash() {
  return <div className="space-y-4"><div className="h-8 w-64 bg-accent rounded" /><div className="grid grid-cols-4 gap-4">{[0,1,2,3].map(i => <div key={i} className="h-28 bg-accent rounded-2xl animate-pulse" />)}</div></div>;
}
